import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MailCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (r?.data?.dev_token) setDevToken(r.data.dev_token);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6">
      <div className="bps-ambient" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-[#9AAEC6] mb-6 hover:text-white transition-colors"
          data-testid="back-to-login"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="mb-8">
          <div className="bps-eyebrow">Forgot password</div>
          <h1 className="font-display text-3xl font-light tracking-tighter mt-2 text-white">
            Reset your password.
          </h1>
          <p className="text-sm text-[#9AAEC6] mt-3 leading-relaxed">
            Enter the email tied to your account. We'll send you a one-time reset link valid for 60 minutes.
          </p>
        </div>

        {!sent ? (
          <form onSubmit={submit} className="space-y-4 bps-card p-6" data-testid="forgot-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#7C8FA8]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="forgot-email"
                className="w-full bg-white/5 rounded-2xl px-4 py-4 mt-1 outline-none focus:ring-2 focus:ring-[#22D3EE] transition-all"
              />
            </div>
            {err && <div className="text-sm text-[#F87171]" data-testid="forgot-error">{err}</div>}
            <button
              disabled={loading}
              data-testid="forgot-submit"
              className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-medium disabled:opacity-30 transition-all hover:-translate-y-0.5"
            >
              {loading ? "Sending…" : "Send reset link"}
            </button>
          </form>
        ) : (
          <div className="bps-card p-6 space-y-4" data-testid="forgot-success">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#22D3EE]/15 flex items-center justify-center">
                <MailCheck size={20} className="text-[#22D3EE]" strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-display text-lg text-white">Check your email</div>
                <div className="text-xs text-[#9AAEC6]">If that account exists, a reset link is on its way.</div>
              </div>
            </div>

            {devToken && (
              <div className="rounded-2xl border border-[#F59E0B]/30 bg-[#F59E0B]/[0.06] p-4 space-y-2" data-testid="dev-token-card">
                <div className="bps-eyebrow text-[#F59E0B]">Dev mode</div>
                <p className="text-xs text-[#9AAEC6] leading-relaxed">
                  Email delivery isn't configured yet. Use this temporary link to continue:
                </p>
                <Link
                  to={`/reset-password?token=${encodeURIComponent(devToken)}`}
                  className="block text-sm text-[#22D3EE] underline break-all"
                  data-testid="dev-reset-link"
                >
                  Open reset page →
                </Link>
              </div>
            )}

            <Link
              to="/login"
              className="block text-center text-sm text-[#22D3EE] mt-2"
              data-testid="forgot-back-link"
            >
              Return to sign in
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
